// frontend/src/pages/public/About/AboutContent.ts

export const aboutHero = {
  eyebrow: "About AcceptingMD",
  title: "A primary care intake workflow built by family doctors",
  subtitle:
    "AcceptingMD is a standardized intake and attachment workflow created by a group of family doctors to support patients seeking a family doctor and clinics managing new patient requests.",
};

export const aboutIntro = {
  id: "intro",
  headline: "AcceptingMD is not a directory or marketplace.",
  subheadline: "It is intake infrastructure.",
  description:
    "The system is designed to reflect how primary care operates across Ontario today — capacity-based, clinic-controlled, and transparent — while reducing confusion for patients and administrative burden for clinics.",
};

export const aboutWhy = {
  id: "why",
  title: "Why AcceptingMD Exists",
  description:
    "Across Ontario, many patients struggle to find a family doctor, while clinics face increasing volumes of unstructured inquiries that are difficult to manage fairly or efficiently.",
  challenges: [
    "Unclear or outdated information about clinic availability",
    "Inconsistent intake processes between clinics",
    "Patient frustration from unclear expectations",
  ],
  conclusion:
    "AcceptingMD provides a single, standardized intake pathway while keeping all attachment decisions with the clinic.",
};

export const aboutWhat = {
  id: "what",
  title: "What We Do",
  description:
    "AcceptingMD enables patients to submit structured requests to participating family medicine clinics.",
  features: [
    "Find doctors who may be accepting new patients",
    "View clear intake information before submitting",
    "Clinics review based on capacity and scope",
    "Receive status updates on your request",
  ],
  note: "All decisions are made independently by clinics.",
};

export const aboutWhatNot = {
  id: "what-not",
  title: "What We Don't Do",
  description: "AcceptingMD is intentionally limited in scope.",
  items: [
    "Guarantee attachment to a doctor",
    "Rank or compare clinics",
    "Assign patients to clinics",
    "Influence clinic decisions",
  ],
  disclaimer:
    "Submitting a request does not guarantee acceptance. Clinics independently determine availability.",
};

export const aboutClinicAutonomy = {
  id: "clinic-autonomy",
  title: "Clinic Autonomy and Patient Clarity",
  clinicControl: {
    description: "Clinics maintain full control over:",
    items: [
      "Their accepting status",
      "Which requests to review",
      "How to respond to each request",
    ],
  },
  patientClarity:
    "Patients see clear information upfront — understanding if a clinic may be a realistic option before submitting.",
  note: "No centralized waitlists. No automatic patient assignment.",
};

export const aboutPrivacy = {
  id: "privacy",
  title: "Privacy First",
  description:
    "Designed to minimize data collection.",
  items: [
    "No health card numbers",
    "No medical records or detailed medical history in the standard online request",
    "No patient accounts",
  ],
  conclusion:
    "The platform is built to support access to primary care while respecting patient privacy and clinic operations.",
};

export const aboutBuiltBy = {
  id: "built-by",
  title: "Built by Family Doctors, for Primary Care",
  description:
    "AcceptingMD was designed by practicing family doctors who understand the operational realities of primary care.",
  mission:
    "The goal is not to promise care, but to support a clear, fair, and capacity-aware intake process that works for patients and clinics alike.",
  tagline: "AcceptingMD supports access — while respecting the limits of the system.",
};

export const aboutCtas = {
  badge: "Built by Family Doctors",
  title: "Ready to get started?",
  subtitle: "AcceptingMD was designed by practicing family doctors who understand the realities of primary care. Our goal is to support a clear, fair intake process that works for patients and clinics alike.",
  primaryCta: { label: "Find a Family Doctor", to: "/public/find-family-doctor" },
  secondaryCta: { label: "How It Works", to: "/public/how-it-works" },
  contactCta: { label: "Contact Us", to: "/public/contact" },
};
